import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext'; 
import { supabase, checkConnection } from '../lib/supabase';

interface ProfileSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  required?: boolean;
}

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  preferences: {
    notifications: boolean;
    newsletter: boolean;
    darkMode: boolean;
    setupComplete: boolean;
  };
  animation_preferences: {
    showCompletionAnimation: boolean;
    animationDuration: number;
    animationType: string;
  };
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ isOpen, onClose, required = false }) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [profile, setProfile] = useState<UserProfile>(() => {
    const savedProfile = localStorage.getItem('userProfile');
    return savedProfile
      ? JSON.parse(savedProfile)
      : {
          firstName: '',
          lastName: '',
          email: '',
          preferences: {
            notifications: true,
            newsletter: true,
            darkMode: false,
            setupComplete: false
          },
          animation_preferences: {
            showCompletionAnimation: true,
            animationDuration: 1500,
            animationType: 'confetti'
          }
        };
  });

  // Load profile from Supabase on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return;
      
      const isConnected = await checkConnection();
      if (!isConnected) {
        console.log('Using cached profile data');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profile_settings')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        if (data) {
          const transformedData = {
            firstName: data.first_name,
            lastName: data.last_name,
            email: data.email,
            preferences: data.preferences,
            animation_preferences: data.animation_preferences
          };
          setProfile(transformedData);
          localStorage.setItem('userProfile', JSON.stringify(transformedData));
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadProfile();
  }, [user?.id]);

  // Persist profile to localStorage
  useEffect(() => {
    localStorage.setItem('userProfile', JSON.stringify(profile));
  }, [profile]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !required) onClose();
    };

    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, required]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !required) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, required]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !required) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, required]);

  const handleSave = async () => {
    setIsSubmitting(true);

    if (!profile.firstName.trim() || !profile.lastName.trim() || !profile.email.trim()) {
      toast.error('Please fill out all fields');
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profile.email)) {
      toast.error('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    try {
      // Validate required fields
      if (!profile.firstName.trim() || !profile.lastName.trim() || !profile.email.trim()) {
        toast.error('Please fill out all required fields');
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('profile_settings')
        .upsert({
          user_id: user.id,
          first_name: profile.firstName,
          last_name: profile.lastName,
          email: profile.email,
          preferences: {
            ...profile.preferences,
            setupComplete: true
          },
          animation_preferences: profile.animation_preferences,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      if (profile.animation_preferences.showCompletionAnimation) {
        setShowAnimation(true);
        setTimeout(() => {
          setShowAnimation(false);
          onClose();
        }, profile.animation_preferences.animationDuration);
      } else {
        onClose();
      }

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile to database. Changes saved locally.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle click outside
  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !required) onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-start justify-center px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(1rem+env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain touch-pan-y"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden my-auto flex flex-col">
        {/* Completion Animation */}
        {showAnimation && (
          <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
            <div className="animate-fade-in">
              <div className="bg-green-500 text-white text-xl font-medium px-6 py-3 rounded-full shadow-lg">
                Profile Setup Complete! 🎉
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-semibold text-gray-800">Profile Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close profile settings"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="space-y-8">
            <section>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={e => setProfile({ ...profile, firstName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={e => setProfile({ ...profile, lastName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your last name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={e => setProfile({ ...profile, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end gap-4">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin">⌛</span>
                Saving...
              </>
            ) : (
              required ? 'Complete Profile Setup' : 'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;